import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { getEnvironment, UprightEnvironment } from '@upright/common/environment';
import { getUiUrl } from '@upright/common/utils';
import { getNiceButtonStyle } from '@upright/ui/common/generic/components/buttons/nice-button';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useGetTimeoutRef } from '@upright/ui/common/generic/hooks/use-get-timeout-ref';
import { FACADE_HOSTNAME } from '@upright/ui/ui-constants';
import { LoginViewLocationState } from '@upright/ui/views/login-views/login-view/login-view-location-state';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { SuccessCheckmark } from '../../../../../generic/components/success-check-mark';
import { useSubscriptionContext } from '../../context';

export const FreeTierSuccessPage = () => {
  const context = useSubscriptionContext();
  const [, setModalOpen] = context.modalOpen;

  const verificationLink = context.submitQuery.data?.emailVerificationLink;
  const environment = getEnvironment();

  const isOnFacade = window.location.host === FACADE_HOSTNAME;

  const LoginLink = isOnFacade ? LinkToPlatformLogin : InternalLinkToLogin;

  return (
    <Root className={'subscriptionForm'}>
      <div className="success">
        <SuccessCheckmark color={BouColor.IMPACT_NEGATIVE_PURPLE} size={'6em'} />
        <h1>User account successfully created!</h1>
        <p>Thank you for signing up to update your company's input data for free. Please log in to input your data.</p>
        <LoginLink testid={E2eTestElementId.SELF_SIGNUP_LOGIN_LINK} setModalOpen={setModalOpen}>
          Log in
        </LoginLink>
        {environment !== UprightEnvironment.AWS_PRODUCTION && (
          <div>
            <p>
              You're in the <strong>{environment}</strong> environment, so here you go with a link to verify the email
              of the just-created user account. Please copy it, log out (if you're not already) and paste it in the
              address bar to verify the user!
            </p>
            <p>{verificationLink || 'NO LINK RECEIVED FROM SERVER'}</p>
          </div>
        )}
      </div>
    </Root>
  );
};

const Root = styled.div`
  button,
  a {
    ${getNiceButtonStyle({ theme: 'dark' })}
    font-weight: 500;
    text-decoration: none;
  }
`;

interface LoginLinkProps {
  children: ReactNode;
  setModalOpen: (newValue: Partial<boolean>) => void;
  testid?: string;
}

const LinkToPlatformLogin = (props: LoginLinkProps) => {
  return (
    <a data-testid={props.testid} href={`${getUiUrl()}/login`}>
      {props.children}
    </a>
  );
};

const InternalLinkToLogin = (props: LoginLinkProps) => {
  const timeoutRef = useGetTimeoutRef();
  const navigate = useNavigate();

  return (
    <button
      data-testid={props.testid}
      onClick={() => {
        props.setModalOpen(false);
        // A minor timeout before resetting, in order to give the modal some time to fade away
        // without its content changing.
        timeoutRef.current = setTimeout(() => {
          const locationState: LoginViewLocationState = { nextUrl: '/' };
          navigate('/login', { state: locationState });
        }, 500);
      }}
    >
      {props.children}
    </button>
  );
};
